import { Box, Card, Divider, List, ListItem, ListItemButton, ListSubheader, Skeleton, Stack, Typography } from '@mui/material'
import { EmptyList } from 'components/core/empty-list/'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1ObjectDisplay } from 'components/core/object-display/C1ObjectDisplay'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName, getEntitlementDisplayNameTextOnly } from 'components/pages/entitlement/display-name'
import { QuickLinksStyles, searchListStyles } from 'components/pages/spotlight/styles'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useLocalStorage } from 'hooks/local-storage/index'
import { AccessReviewValue } from 'lib/actions/access_review'
import { AppValue } from 'lib/actions/apps'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { UserValue } from 'lib/actions/user'
import { ArrowDown, ArrowUp, CornerDownLeft, Gavel } from 'lucide-react'
import { useRouter } from 'next/router'
import { FC, ReactNode, useRef, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { QuickLinks, SearchResult, SPOTLIGHT_SEARCH_STORAGE_KEY } from './QuickLinks'

const getUpdatedRecentSearch = (item: SearchResult, existing: Array<SearchResult> | null): Array<SearchResult> => {
    if (existing === null) {
        return [item]
    }

    // If already exists, rearrange the filtered item to be on top
    if (existing.filter((e) => e.path === item.path).length > 0) {
        const filtered = existing.filter((search) => search.path !== item.path)
        return [item, ...filtered]
    }

    // Else add the item to the top of the list
    const updatedRecentSearches = [item, ...existing]
    return updatedRecentSearches.slice(0, 5)
}

const UserItem = ({ item, goTo }: { item: UserValue, goTo: (path: string) => void }): React.ReactElement => {
    const [recentSearches, saveRecentSearch] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])

    return <ListItem disablePadding key={item.id}>
        <ListItemButton
            data-dd-action-name="Select user"
            onClick={(): void => {
                const path = `/admin/directory/users/${item.id}`
                const itemToAdd = { name: item.displayName, path }
                saveRecentSearch(getUpdatedRecentSearch(itemToAdd, recentSearches))
                goTo(path)
            }}
            sx={QuickLinksStyles.listItem}
        >
            <UserDisplayName user={item} readOnly margin={false} />
        </ListItemButton>
    </ListItem>
}

const EntitlementItem = ({ item, goTo }: { item: AppEntitlementValue, goTo: (path: string) => void }): React.ReactElement => {
    const [recentSearches, saveRecentSearch] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])

    return <ListItem disablePadding key={item.id}>
        <ListItemButton
            onClick={(): void => {
                const path = `/admin/applications/${item.appId}/entitlements/${item.id}`
                // ask Santhosh / Magizh if this is right. This is what entitlement details page uses
                const itemToAdd = { name: getEntitlementDisplayNameTextOnly(item), path }
                saveRecentSearch(getUpdatedRecentSearch(itemToAdd, recentSearches))
                goTo(path)
            }}
            sx={QuickLinksStyles.listItem}
        >
            <C1EntitlementDisplayName entitlement={item} readOnly />
        </ListItemButton>
    </ListItem>
}

const AppItem = ({ item, goTo }: { item: AppValue, goTo: (path: string) => void }): React.ReactElement => {
    const [recentSearches, saveRecentSearch] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])

    return <ListItem disablePadding key={item.id}>
        <ListItemButton
            onClick={(): void => {
                const path = `/admin/applications/${item.id}`
                const itemToAdd = { name: item.displayName, path }
                saveRecentSearch(getUpdatedRecentSearch(itemToAdd, recentSearches))
                goTo(path)
            }}
            sx={QuickLinksStyles.listItem}
        >
            <AppDisplayName app={item} readOnly avatarSize="xs" margin={false} />
        </ListItemButton>
    </ListItem>
}

const CampaignItem = ({ item, goTo }: { item: AppEntitlementValue, goTo: (path: string) => void }): React.ReactElement => {
    const [recentSearches, saveRecentSearch] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY, [])

    return <ListItem disablePadding key={item.id}>
        <ListItemButton
            onClick={(): void => {
                const path = `/admin/campaigns/${item.id}`
                const itemToAdd = { name: item.displayName, path }
                saveRecentSearch(getUpdatedRecentSearch(itemToAdd, recentSearches))
                goTo(path)
            }}
            sx={QuickLinksStyles.listItem}
        >
            <C1ObjectDisplay avatar={<C1Icon icon={Gavel} size="sm" />} title={item.displayName} />
        </ListItemButton>
    </ListItem>
}

type ListSectionProps<T> = {
    title: string
    items: readonly AppEntitlementValue[] | readonly UserValue[] | readonly AccessReviewValue[] | readonly AppValue[]
    goTo: (path: string) => void
    Component: FC<{ item: T, goTo: (path: string) => void }>
}

const ListSection = <T, >({ title, items, Component, goTo }: ListSectionProps<T>): ReactNode => {
    if (!items.length) {
        return null
    }

    return (
        <Box pt={3} key={title}>
            <List
                subheader={
                    <ListSubheader id="results-list-subheader" sx={QuickLinksStyles.header}>
                        {title}
                    </ListSubheader>
                }
                key={title}
            >
                {items.slice(0, 3).map((item) => (
                    <Component item={item} goTo={goTo} key={item.id} />
                ))}
            </List>
        </Box>
    )
}

type ResultsProps = {
    entitlements: readonly AppEntitlementValue[]
    users: readonly UserValue[]
    campaigns: readonly AccessReviewValue[]
    apps: readonly AppValue[]
    goTo: (path: string) => void
    loadingErrorText: string
}

const Results: FC<ResultsProps> = ({ entitlements, users, campaigns, apps, goTo, loadingErrorText }) => {
    const intl = useIntl()
    const hasUsers = users && users.length > 0
    const hasEntitlements = entitlements && entitlements.length > 0
    const hasApps = apps && apps.length > 0
    const hasCampaigns = campaigns && campaigns.length > 0

    const listRef = useRef<HTMLElement>(null)

    useEffect(() => {
        const list = listRef.current
        if (!list) {
            return
        }
        const handleKeyDown = (event): void => {
            if (event.key === 'ArrowDown') {
                const activeElement = document.activeElement
                const nodes = list.querySelectorAll('[role="button"]')
                let currentIndex = Array.from(nodes).findIndex((item) => item === activeElement)
                currentIndex = currentIndex === -1 ? -1 : currentIndex
                if (currentIndex < nodes.length - 1) {
                    event.preventDefault()
                    const nextItem = nodes[currentIndex + 1] as HTMLElement
                    nextItem.focus()
                }
            }

            if (event.key === 'ArrowUp') {
                const activeElement = document.activeElement
                const nodes = list.querySelectorAll('[role="button"]')
                let currentIndex = Array.from(nodes).findIndex((item) => item === activeElement)
                currentIndex = currentIndex === -1 ? nodes.length : currentIndex
                if (currentIndex > 0) {
                    event.preventDefault()
                    const nextItem = nodes[currentIndex - 1] as HTMLElement
                    nextItem.focus()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    return (
        <Box ref={listRef}>
            {
                hasUsers && <ListSection<UserValue> title={intl.formatMessage({ id: 'users', defaultMessage: 'Users' })} items={users} Component={UserItem} goTo={goTo} />
            }
            {
                hasEntitlements && <>
                    {hasUsers && <Divider sx={searchListStyles.divider} />}
                    <ListSection<AppEntitlementValue> title={intl.formatMessage({ id: 'entitlements', defaultMessage: 'Entitlements' })} items={entitlements} Component={EntitlementItem} goTo={goTo} />
                </>
            }
            {
                hasApps && <>
                    {(hasUsers || hasEntitlements) && <Divider sx={searchListStyles.divider} />}
                    <ListSection<AppValue> title={intl.formatMessage({ id: 'apps', defaultMessage: 'Applications' })} items={apps} Component={AppItem} goTo={goTo} />
                </>
            }
            {
                hasCampaigns && <>
                    {(hasUsers || hasEntitlements || hasApps) && <Divider sx={searchListStyles.divider} />}
                    <ListSection<AppEntitlementValue> title={intl.formatMessage({ id: 'campaigns', defaultMessage: 'Campaigns' })} items={campaigns} Component={CampaignItem} goTo={goTo} />
                </>
            }
            {loadingErrorText && <Box pt={3}><MinimalAPIError err={<Typography>{loadingErrorText}</Typography>} /></Box>}
        </Box>
    )
}

type ListSearchResultsProps = {
    entitlements: readonly AppEntitlementValue[]
    users: readonly UserValue[]
    campaigns: readonly AccessReviewValue[]
    apps: readonly AppValue[]
    isLoading: boolean
    isEmpty: boolean
    handleClose: () => void,
    loadingErrorText: string
}

export const ListSearchResults: FC<ListSearchResultsProps> = ({
    entitlements,
    users,
    campaigns,
    apps,
    isLoading,
    isEmpty,
    handleClose,
    loadingErrorText,
}): React.ReactElement => {
    const intl = useIntl()
    const router = useRouter()
    const goTo = (path: string): void => {
        router.push(path)
        handleClose()
    }

    if (isLoading) {
        return <Stack spacing={4} pt={5}>
            {[...Array(3)].map((_, i) => (<Stack key={i} spacing={1}>
                <Skeleton animation="wave" width={100} />
                <Skeleton animation="wave" width={300} />
            </Stack>))}
        </Stack>
    }

    if (isEmpty) {
        return <QuickLinks goTo={goTo} />
    }
    const hasUsers = users && users.length > 0
    const hasEntitlements = entitlements && entitlements.length > 0
    const hasCampaigns = campaigns && campaigns.length > 0
    const hasApps = apps && apps.length > 0

    if (!hasUsers && !hasEntitlements && !hasCampaigns && !hasApps) {
        return <Box pt={3}><EmptyList /></Box>
    }

    return <>
        <Card
            variant="outlined"
            sx={searchListStyles.keyNav}
            role="note"
            aria-label={intl.formatMessage({ id: 'spotlight_keyboard_shortcuts', defaultMessage: 'Keyboard shortcuts' })}
        >
            <Stack spacing={3} p={2} direction="row" alignItems="center">
                <Stack spacing={1} direction="row" alignItems="center">
                    <C1Icon
                        icon={ArrowUp}
                        size="sm"
                        sx={searchListStyles.icon}
                    />
                    <C1Icon
                        icon={ArrowDown}
                        size="sm"
                        sx={searchListStyles.icon}
                    />
                    <Typography>
                        <FormattedMessage id="spotlight_keyboard_nav" defaultMessage="to navigate" />
                    </Typography>
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                    <C1Icon
                        icon={CornerDownLeft}
                        size="sm"
                        sx={searchListStyles.icon}
                    />
                    <Typography>
                        <FormattedMessage id="spotlight_keyboard_nav" defaultMessage="to select" />
                    </Typography>
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                    <Card variant="outlined" sx={searchListStyles.esc}>
                        <Typography variant="caption">
                            ESC
                        </Typography>
                    </Card>
                    <Typography>
                        <FormattedMessage id="spotlight_keyboard_nav" defaultMessage="to dismiss" />
                    </Typography>
                </Stack>
            </Stack>
        </Card>
        <Results
            entitlements={entitlements}
            users={users}
            campaigns={campaigns}
            apps={apps}
            goTo={goTo}
            loadingErrorText={loadingErrorText}
        />
    </>
}
